import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import HomeHeader from '../../components/header/HomeHeader';
import CommonFooter from '../../components/footer/CommonFooter';
import CommonInput from '../../components/input/CommonInput';
import PhoneInput from '../../components/input/PhoneInput';
import DocumentUpload from '../../components/input/DocumentUpload';
import CommonButton from '../../components/button/CommonButton';
import Card from '../../components/card';

import {
  handleNumberInputWithoutDecimal,
  handleUpperCaseNoSpace,
  getErrorMessage
} from '../../constants/globalFunction';
import { appApiHandler } from '../../api/appApiHandler';
import { setUserData } from '../../store/slices/userState';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { FaUser, FaEnvelope } from 'react-icons/fa';
import { BASE_URL } from '../../constants/configConstants';
import './Profile.css';
import { string } from '../../constants/string';
import Breadcrumb from '../../components/breadcrumb';

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.userState);

  const [name, setName] = useState(userData?.name || '');
  const [email, setEmail] = useState(userData?.email || '');
  const [phoneno, setPhoneno] = useState(userData?.phoneno || '');
  const [countryCode, setCountryCode] = useState(userData?.country_code || '+91');
  const [country, setCountry] = useState(userData?.country || 'India');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phonenoError, setPhonenoError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gst, setGst] = useState(null);
  const [gstNo, setGstNo] = useState(userData?.gst_no || '');
  const [gstNoError, setGstNoError] = useState('');
  const [gstError, setGstError] = useState('');
  const [panCard, setPanCard] = useState(null);
  const [panCardNo, setPanCardNo] = useState(userData?.pancard_no || '');
  const [panCardNoError, setPanCardNoError] = useState('');
  const [panCardError, setPanCardError] = useState('');
  const [passport, setPassport] = useState(null);
  const [passportNo, setPassportNo] = useState(userData?.passport_no || '');
  const [passportNoError, setPassportNoError] = useState('');
  const [passportError, setPassportError] = useState('');
  const [profileImage, setProfileImage] = useState(null);

  const onChangePhoneno = (text) => {
    setPhoneno(handleNumberInputWithoutDecimal(text));
  };

  const validateValues = () => {
    let isValid = true;

    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError('');
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter phone number");
      isValid = false;
    } else {
      setPhonenoError('');
    }

    if (countryCode === "+91") {
      const digitsOnlyLength = phoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (userData?.user_type === 'wholeseller' && countryCode === '+91') {
      if (!userData?.gst_no && !gstNo.trim()) {
        setGstNoError("Please enter GST number");
        isValid = false;
      }
      if (!userData?.gst && !gst) {
        setGstError("Please upload GST document");
        isValid = false;
      }
      if (!userData?.pancard_no && !panCardNo.trim()) {
        setPanCardNoError("Please enter PAN card number");
        isValid = false;
      }
      if (!userData?.pancard && !panCard) {
        setPanCardError("Please upload PAN card document");
        isValid = false;
      }
    }

    if (userData?.user_type === 'wholeseller' && countryCode !== '+91') {
      if (!userData?.passport_no && !passportNo.trim()) {
        setPassportNoError("Please enter passport number");
        isValid = false;
      }
      if (!userData?.passport && !passport) {
        setPassportError("Please upload passport document");
        isValid = false;
      }
    }

    return isValid;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateValues();
    if (isValid) {
      try {
        const reqData = new FormData();
        reqData.append('user_id', userData?._id);
        reqData.append('name', name.trim());
        reqData.append('phoneno', phoneno.trim());
        reqData.append('country_code', countryCode.trim());
        reqData.append('country', country.trim());

        if (profileImage) {
          reqData.append('profile_image', profileImage);
        }

        if (userData?.user_type === 'wholeseller' && countryCode === '+91') {
          if (gst) {
            reqData.append('gst', gst);
          }
          reqData.append('gst_no', gstNo.trim());
          if (panCard) {
            reqData.append('pancard', panCard);
          }
          reqData.append('pancard_no', panCardNo.trim());
          reqData.append('passport', null);
          reqData.append('passport_no', '');
        } else if (userData?.user_type === 'wholeseller' && countryCode !== '+91') {
          if (passport) {
            reqData.append('passport', passport);
          }
          reqData.append('passport_no', passportNo.trim());
          reqData.append('gst', null);
          reqData.append('gst_no', '');
          reqData.append('pancard', null);
          reqData.append('pancard_no', '');
        }

        setLoading(true);
        const response = await appApiHandler.updateProfile(reqData);
        setLoading(false);

        if (response?.status === 200) {
          dispatch(setUserData(response?.data));
          showSuccessToast('Profile updated successfully');
          navigate('/');
        } else if (response?.message) {
          showErrorToast(getErrorMessage(response?.message));
        } else {
          showErrorToast(string.error.failed_due_to_internal_error);
        }
      } catch (err) {
        setLoading(false);
        showErrorToast(string.error.profile_update_failed);
      }
    }
  };

  const handleFileUpload = (setFile, setError) => (file) => {
    if (file) {
      setFile(file);
      setError('');
    }
  };

  const onChangeCountryCode = (country) => {
    setCountryCode(country.dial_code);
    setCountry(country.name);
    if (country.dial_code === '+91') {
      setPassport(null);
      setPassportError('');
    } else {
      setGst(null);
      setGstError('');
      setPanCard(null);
      setPanCardError('');
    }
  };

  const onChangeGstNo = (text) => {
    setGstNo(handleUpperCaseNoSpace(text));
  };

  const onChangePanCardNo = (text) => {
    setPanCardNo(handleUpperCaseNoSpace(text));
  };

  const onChangePassportNo = (text) => {
    setPassportNo(handleUpperCaseNoSpace(text));
  };

  const handleDeleteProfileImage = async () => {
    const isConfirmed = window.confirm('Are you sure you want to delete your profile photo?');
    if (isConfirmed) {
      try {
        setLoading(true);
        const response = await appApiHandler.deleteProfileImage({ user_id: userData?._id });
        setLoading(false);

        if (response?.status === 200) {
          const updatedUserData = { ...userData, profile_image: null };
          dispatch(setUserData(updatedUserData));
          showSuccessToast('Profile image deleted successfully');
        } else if (response?.message) {
          showErrorToast(getErrorMessage(response?.message));
        } else {
          showErrorToast(string.error.failed_due_to_internal_error);
        }
      } catch (err) {
        setLoading(false);
        showErrorToast('Failed to delete profile image');
      }
    }
  };

  return (
    <div className="profile-page">
      <HomeHeader />
      <div className="page-content">
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Profile",
              isActive: true,
            },
          ]}
        />
        <Card>
          <form onSubmit={onSubmit}>
            {userData?.profile_image ? (
              <div className="profile-image-section">
                <div className="profile-image-container">
                  <img
                    src={`${BASE_URL}${userData.profile_image}`}
                    alt="Profile"
                    className="profile-image-display"
                  />
                  <button
                    type="button"
                    className="delete-photo-btn"
                    onClick={handleDeleteProfileImage}
                    disabled={loading}
                  >
                    Delete Photo
                  </button>
                </div>
              </div>
            ) : (
              <DocumentUpload
                label="Profile Image"
                required={false}
                value={profileImage}
                onChange={(file) => setProfileImage(file)}
                disabled={loading}
                placeholder="Select Profile Image"
              />
            )}

            <CommonInput
              label="Name"
              value={name}
              onChange={setName}
              error={nameError}
              placeholder="Enter name"
              leftIcon={<FaUser className="profile-icon" />}
            />

            <CommonInput
              label="Email"
              value={email}
              onChange={() => { }}
              type="email"
              disabled={true}
              editable={false}
              error={emailError}
              placeholder="Enter email"
              leftIcon={<FaEnvelope className="profile-icon" />}
            />

            <PhoneInput
              label="Phone number"
              code={countryCode}
              onChangeCode={onChangeCountryCode}
              onChangeText={onChangePhoneno}
              value={phoneno}
              placeholder="Enter phone number"
              error={phonenoError}
              disabled={loading}
            />

            {userData?.user_type === 'wholeseller' && countryCode === '+91' && (
              <>
                <CommonInput
                  label="GST Number"
                  value={gstNo}
                  onChange={onChangeGstNo}
                  error={gstNoError}
                  placeholder="Enter GST number"
                />

                <DocumentUpload
                  value={gst}
                  onChange={handleFileUpload(setGst, setGstError)}
                  disabled={loading}
                  label="GST"
                  error={gstError}
                  required={true}
                  uploadedDoc={userData?.gst}
                />

                <CommonInput
                  label="PAN Card Number"
                  value={panCardNo}
                  onChange={onChangePanCardNo}
                  error={panCardNoError}
                  placeholder="Enter PAN card number"
                />

                <DocumentUpload
                  value={panCard}
                  onChange={handleFileUpload(setPanCard, setPanCardError)}
                  disabled={loading}
                  label="PAN Card"
                  error={panCardError}
                  required={true}
                  uploadedDoc={userData?.pancard}
                />
              </>
            )}

            {userData?.user_type === 'wholeseller' && countryCode !== '+91' && (
              <>
                <CommonInput
                  label="Passport Number"
                  value={passportNo}
                  onChange={onChangePassportNo}
                  error={passportNoError}
                  placeholder="Enter passport number"
                />

                <DocumentUpload
                  value={passport}
                  onChange={handleFileUpload(setPassport, setPassportError)}
                  disabled={loading}
                  label="Passport"
                  error={passportError}
                  required={true}
                  uploadedDoc={userData?.passport}
                />
              </>
            )}

            <CommonButton
              title="Save"
              type="submit"
              isLoading={loading}
              disabled={loading}
            />
          </form>
        </Card>
      </div>
      <CommonFooter />
    </div>
  );
};

export default Profile;